/**
 * AudienceController — bootstrap de la mesure d'audience §26 côté application
 * (EPIC-040 / 40.9).
 *
 * Controller global monté sur le `<body>` des layouts app / auth / blank.
 * Au montage : récupère les catégories d'audience grossières du visiteur
 * (`accountType` / `accountJourney`) via `GET /v1/me/audience-context`,
 * puis initialise le SDK partagé `window.audienceSdk` (chargé en `<head>`),
 * ce qui déclenche le `pageview` et l'écoute des clics `[data-track]`.
 *
 * Strictement distinct du tracker in-app `conversion_event` §2.K
 * (`conversion-tracker_controller.js`) : pas d'identifiant nominatif, pas de
 * session partagée, pas de jointure — c'est un choix de conformité §26.
 *
 * Robustesse : toute erreur (endpoint, réseau, SDK absent) est avalée. La
 * mesure d'audience ne doit jamais casser une page applicative.
 */

import BaseController from "./base_controller.js";
import { API_BASE_URL } from "../services/api.js";

/**
 * URL du endpoint `/collect` du service `tracker` (mesure d'audience §26).
 * Injectée au runtime via window.APP_CONFIG.trackerUrl (route /config.js de
 * serve.js, var TRACKER_COLLECT_URL) — même mécanisme 12-factor que apiBaseUrl,
 * pour que la préprod n'envoie pas ses beacons vers la prod. Repli sur l'URL
 * prod si la config n'a pas été chargée ; un échec d'envoi reste silencieux.
 */
const TRACKER_COLLECT_URL =
  window.APP_CONFIG?.trackerUrl ?? "https://tracker.notamicro.fr/collect";

export default class AudienceController extends BaseController {
  async connect() {
    // Idempotent : un seul bootstrap même si plusieurs <body> existaient.
    if (window.__audienceInitialized) return;
    window.__audienceInitialized = true;

    // Le SDK est chargé en <head> (defer) avant les modules — mais on reste
    // défensif si le script a échoué au chargement.
    if (!window.audienceSdk) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/audience-context`, {
        credentials: "include",
      });
      if (!response.ok) return;
      const context = await response.json();

      window.audienceSdk.init({
        endpoint: TRACKER_COLLECT_URL,
        source: "app",
        context: {
          accountType: context.accountType,
          accountJourney: context.accountJourney,
        },
      });
    } catch (err) {
      // Silencieux — la mesure d'audience ne doit jamais casser une page.
    }
  }
}
